import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { isPushSupported, getNotificationPermission, requestAndSubscribe } from '../lib/pushNotifications';
import { useAuth } from '../context/AuthContext';

const DISMISSED_KEY = 'push_permission_dismissed';

export default function PushPermissionPrompt() {
  const { session, gym, isDemoMode, profile } = useAuth();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 비로그인, 데모, 어드민, 멤버 역할은 표시하지 않음
    if (!session || isDemoMode) return;
    if (profile?.role === 'admin' || profile?.role === 'member') return;
    if (!isPushSupported()) return;
    if (getNotificationPermission() !== 'default') return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    // 로그인 2초 후 표시
    const timer = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timer);
  }, [session, isDemoMode, profile?.role]);

  const handleAllow = async () => {
    setLoading(true);
    const gymId = gym?.id;
    if (gymId) {
      await requestAndSubscribe(gymId);
    } else {
      await Notification.requestPermission();
    }
    setLoading(false);
    setShow(false);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              알림을 허용하시겠습니까?
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              회원권 만료, 납부 승인, PT 잔여 알림을 받아보세요.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleAllow}
                disabled={loading}
                className="flex-1 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? '처리 중...' : '허용하기'}
              </button>
              <button
                onClick={handleDismiss}
                className="flex-1 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 text-xs font-medium transition-colors"
              >
                나중에
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
